import React, { useState } from 'react';
import {
  Box, Container, Grid, Card, CardContent, Typography, TextField,
  MenuItem, Button, Alert, Stack, CircularProgress,
} from '@mui/material';
import { CheckCircle, CalendarMonth, Phone } from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { departments, doctors, emergencyNumbers } from '../data/hospitalData';
import PageHero from '../components/common/PageHero';

interface FormData {
  fullName: string;
  phone: string;
  email: string;
  department: string;
  doctor: string;
  date: string;
  message: string;
}

interface FormErrors {
  fullName?: string;
  phone?: string;
  email?: string;
  department?: string;
  date?: string;
}

const AppointmentPage: React.FC = () => {
  const { t, language } = useLanguage();
  const [form, setForm] = useState<FormData>({
    fullName: '', phone: '', email: '', department: '', doctor: '', date: '', message: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!form.fullName.trim()) newErrors.fullName = language === 'en' ? 'Full name is required' : 'Le nom complet est requis';
    if (!form.phone.trim()) newErrors.phone = language === 'en' ? 'Phone number is required' : 'Le numéro de téléphone est requis';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      newErrors.email = language === 'en' ? 'Valid email is required' : 'Un email valide est requis';
    if (!form.department) newErrors.department = language === 'en' ? 'Please select a department' : 'Veuillez sélectionner un département';
    if (!form.date) newErrors.date = language === 'en' ? 'Preferred date is required' : 'La date préférée est requise';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    if (errors[field as keyof FormErrors]) setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    await new Promise(r => setTimeout(r, 1500)); // simulate API call
    setLoading(false);
    setSubmitted(true);
  };

  const today = new Date().toISOString().split('T')[0];

  if (submitted) {
    return (
      <Box>
        <PageHero title={t.appointment.title} subtitle={t.appointment.subtitle} />
        <Container maxWidth="sm" sx={{ py: 10, textAlign: 'center' }}>
          <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
          <Typography variant="h4" fontWeight={700} mb={2}>{t.appointment.successTitle}</Typography>
          <Typography color="text.secondary" lineHeight={1.8}>{t.appointment.successMsg}</Typography>
          <Button variant="contained" sx={{ mt: 4 }} onClick={() => { setSubmitted(false); setForm({ fullName: '', phone: '', email: '', department: '', doctor: '', date: '', message: '' }); }}>
            {language === 'en' ? 'Book Another' : 'Nouvelle Demande'}
          </Button>
        </Container>
      </Box>
    );
  }

  const apptSubCards = [
    { icon: '📅', titleEn: 'Outpatient Consultation', titleFr: 'Consultation Externe', bgColor: '#0B4F8A' },
    { icon: '👨‍⚕️', titleEn: 'Specialist Appointment', titleFr: 'Rendez-vous Spécialiste', bgColor: '#1565C0' },
    { icon: '🔬', titleEn: 'Diagnostic Tests', titleFr: 'Examens Diagnostiques', bgColor: '#0277BD' },
    { icon: '🏥', titleEn: 'Planned Hospitalization', titleFr: 'Hospitalisation Programmée', bgColor: '#01579B' },
  ];

  return (
    <Box sx={{ bgcolor: 'background.default' }}>
      <PageHero
        title={t.appointment.title}
        subtitle={t.appointment.subtitle}
        breadcrumbs={[
          { label: language === 'en' ? 'Home' : 'Accueil', path: '/' },
          { label: t.nav.appointment },
        ]}
        accentColor="#0B4F8A"
      />

      {/* Sub-category cards */}
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Grid container spacing={2}>
          {apptSubCards.map((card, i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Box sx={{ border: '1px solid', borderColor: 'divider', overflow: 'hidden', height: '100%' }}>
                <Box sx={{ height: 140, background: `linear-gradient(135deg, ${card.bgColor}CC 0%, ${card.bgColor}88 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem' }}>
                  {card.icon}
                </Box>
                <Box sx={{ p: 2 }}>
                  <Box sx={{ borderLeft: '3px solid', borderColor: 'primary.main', pl: 1.5 }}>
                    <Typography variant="subtitle2" fontWeight={700} lineHeight={1.3}>
                      {language === 'en' ? card.titleEn : card.titleFr}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Container maxWidth="xl" sx={{ pb: 8 }}>
        <Grid container spacing={4}>
          {/* Form */}
          <Grid item xs={12} md={8}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                <Typography variant="h5" fontWeight={700} mb={3}>
                  {language === 'en' ? 'Appointment Details' : 'Détails du Rendez-vous'}
                </Typography>
                <Grid container spacing={2.5}>
                  {/* Row 1 — Personal info */}
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth label={t.appointment.fullName} required
                      value={form.fullName} onChange={handleChange('fullName')}
                      error={!!errors.fullName} helperText={errors.fullName}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth label={t.appointment.phone} required
                      value={form.phone} onChange={handleChange('phone')}
                      error={!!errors.phone} helperText={errors.phone}
                      placeholder="+237 6XX XXX XXX"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth label={t.appointment.email} type="email" required
                      value={form.email} onChange={handleChange('email')}
                      error={!!errors.email} helperText={errors.email}
                    />
                  </Grid>

                  {/* Row 2 — Department & Doctor */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select fullWidth label={t.appointment.department} required
                      value={form.department} onChange={handleChange('department')}
                      error={!!errors.department} helperText={errors.department}
                    >
                      <MenuItem value="">{t.appointment.selectDept}</MenuItem>
                      {departments.map(d => (
                        <MenuItem key={d.id} value={d.id}>
                          {language === 'en' ? d.nameEn : d.nameFr}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select fullWidth label={t.appointment.doctor}
                      value={form.doctor} onChange={handleChange('doctor')}
                    >
                      <MenuItem value="">{t.appointment.selectDoctor}</MenuItem>
                      {doctors.map(d => (
                        <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/* Row 3 — Date & Message */}
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth label={t.appointment.date} type="date" required
                      value={form.date} onChange={handleChange('date')}
                      error={!!errors.date} helperText={errors.date}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: today }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <TextField
                      fullWidth label={t.appointment.message} multiline rows={4}
                      value={form.message} onChange={handleChange('message')}
                      placeholder={t.appointment.messagePlaceholder}
                    />
                  </Grid>

                  {/* Submit */}
                  <Grid item xs={12}>
                    <Button
                      variant="contained" size="large"
                      onClick={handleSubmit} disabled={loading}
                      startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CalendarMonth />}
                      sx={{ px: 5, py: 1.5, fontWeight: 700, textTransform: 'none', borderRadius: 1 }}
                    >
                      {loading ? t.common.loading : t.appointment.submitBtn}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} md={4}>
            <Stack spacing={3}>
              <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" fontWeight={700} mb={2}>
                    {language === 'en' ? 'Need Help?' : 'Besoin d\'Aide ?'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mb: 2, lineHeight: 1.7 }}>
                    {language === 'en'
                      ? 'Call us directly to book your appointment or for any inquiries.'
                      : 'Appelez-nous directement pour prendre rendez-vous ou pour toute question.'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Phone />
                    <Typography fontWeight={700}>{emergencyNumbers.main}</Typography>
                  </Box>
                </CardContent>
              </Card>

              <Card>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="subtitle1" fontWeight={700} mb={2}>
                    {language === 'en' ? 'What to Expect' : 'Ce à Quoi S\'attendre'}
                  </Typography>
                  {[
                    language === 'en' ? 'Submit your appointment request' : 'Soumettez votre demande',
                    language === 'en' ? 'Receive confirmation within 24h' : 'Recevez confirmation sous 24h',
                    language === 'en' ? 'Arrive 15 minutes before your appointment' : 'Arrivez 15 min avant le rendez-vous',
                    language === 'en' ? 'Bring your ID and medical records' : 'Apportez ID et dossier médical',
                  ].map((step, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                      <Box
                        sx={{
                          width: 24, height: 24, borderRadius: '50%',
                          bgcolor: 'primary.main', color: 'white',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '0.72rem', fontWeight: 700, flexShrink: 0,
                        }}
                      >
                        {i + 1}
                      </Box>
                      <Typography variant="body2" color="text.secondary" lineHeight={1.5}>{step}</Typography>
                    </Box>
                  ))}
                </CardContent>
              </Card>

              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <Typography variant="body2">
                  {language === 'en'
                    ? 'For emergencies, please call our emergency line directly rather than booking an appointment.'
                    : 'Pour les urgences, veuillez appeler notre ligne d\'urgence directement plutôt que de prendre rendez-vous.'}
                </Typography>
              </Alert>
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default AppointmentPage;
